/**
 * 이미지 파일의 헤더만 읽어 화면에 보일 가로 세로와 EXIF 방향을 구한다.
 * 픽셀을 디코드하지 않는다.
 *
 * 왜 필요한가: 축소 디코드(createImageBitmap 의 resizeWidth 및 resizeHeight)는
 * 목표 크기를 알아야 쓸 수 있는데, 크기를 알자고 먼저 한 번 디코드하면 줄이려던
 * 비용을 그대로 치른다. 헤더는 수십 KB 라 그 왕복이 없다.
 *
 * 방향까지 읽는 이유: 폰 카메라는 세워 찍어도 센서 기준 가로로 저장하고 회전은
 * EXIF 플래그로만 남긴다. 그래서 SOF 가 말하는 8064x6048 이 화면에서는
 * 6048x8064 일 수 있다. 그 둘을 구분하지 않고 SOF 값으로 목표 크기를 잡으면
 * 세로 사진이 가로로 찌그러진다.
 *
 * JPEG 과 PNG 만 읽는다. 그 밖의 형식은 null 을 돌려주고, 호출부가 기존 경로로
 * 되돌아간다 — 못 읽는 형식 때문에 업로드가 막히지는 않는다.
 */
export interface ImageMeta {
  /** EXIF 방향을 반영한, 화면에 보일 너비 */
  width: number;
  /** EXIF 방향을 반영한, 화면에 보일 높이 */
  height: number;
  /** EXIF orientation (1-8). 없으면 1 */
  orientation: number;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

// 크기를 담은 SOF 마커들. SOF4(C4) 는 허프만 테이블, SOF8(C8) 은 JPEG 확장,
// SOFC(CC) 는 산술 부호화 조건이라 크기를 담지 않으므로 빠져 있다.
const SOF_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

const EXIF_ORIENTATION_TAG = 0x0112;

/** 5 에서 8 은 90도 계열 회전이라 가로 세로가 뒤바뀐다. */
export function orientationSwapsAxes(orientation: number): boolean {
  return orientation >= 5 && orientation <= 8;
}

function readPngSize(view: DataView): ImageMeta | null {
  if (view.byteLength < 24) return null;
  for (let i = 0; i < PNG_SIGNATURE.length; i += 1) {
    if (view.getUint8(i) !== PNG_SIGNATURE[i]) return null;
  }
  // PNG 에는 EXIF 방향 관행이 없다.
  return { width: view.getUint32(16), height: view.getUint32(20), orientation: 1 };
}

/** APP1 세그먼트 안의 TIFF 블록에서 orientation 태그만 꺼낸다. */
function readExifOrientation(view: DataView, start: number, length: number): number | null {
  // "Exif\0\0"
  if (start + 6 > view.byteLength) return null;
  const isExif =
    view.getUint8(start) === 0x45 && view.getUint8(start + 1) === 0x78 &&
    view.getUint8(start + 2) === 0x69 && view.getUint8(start + 3) === 0x66 &&
    view.getUint8(start + 4) === 0x00 && view.getUint8(start + 5) === 0x00;
  if (!isExif) return null;

  const tiff = start + 6;
  if (tiff + 8 > view.byteLength) return null;
  const byteOrder = view.getUint16(tiff);
  // "II" = little endian, "MM" = big endian. 그 밖이면 EXIF 가 아니다.
  if (byteOrder !== 0x4949 && byteOrder !== 0x4d4d) return null;
  const little = byteOrder === 0x4949;
  if (view.getUint16(tiff + 2, little) !== 0x002a) return null;

  const ifdOffset = view.getUint32(tiff + 4, little);
  const ifd = tiff + ifdOffset;
  // 오프셋은 TIFF 블록 기준이므로 세그먼트 밖을 가리키면 버린다.
  if (ifd + 2 > view.byteLength || ifd + 2 > start + length) return null;

  const entries = view.getUint16(ifd, little);
  for (let i = 0; i < entries; i += 1) {
    const entry = ifd + 2 + i * 12;
    if (entry + 12 > view.byteLength) return null;
    if (view.getUint16(entry, little) === EXIF_ORIENTATION_TAG) {
      // SHORT 1개라 값이 오프셋 자리에 그대로 들어 있다.
      const value = view.getUint16(entry + 8, little);
      return value >= 1 && value <= 8 ? value : null;
    }
  }
  return null;
}

function readJpegMeta(view: DataView): ImageMeta | null {
  if (view.byteLength < 4) return null;
  if (view.getUint8(0) !== 0xff || view.getUint8(1) !== 0xd8) return null;

  let offset = 2;
  let orientation = 1;
  while (offset + 3 < view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      offset += 1;
      continue;
    }
    const marker = view.getUint8(offset + 1);
    // 0xFF 가 연달아 오는 것은 패딩이고, D0-D9 는 길이 없는 독립 마커다.
    if (marker === 0xff) {
      offset += 1;
      continue;
    }
    if (marker >= 0xd0 && marker <= 0xd9) {
      offset += 2;
      continue;
    }
    const length = view.getUint16(offset + 2);
    if (length < 2) return null;

    // APP1 은 SOF 보다 앞에 오므로, SOF 에 닿을 때면 방향을 이미 읽어 둔 상태다.
    if (marker === 0xe1) {
      orientation = readExifOrientation(view, offset + 4, length) ?? orientation;
    }

    if (SOF_MARKERS.has(marker)) {
      // 세그먼트: [길이 2][정밀도 1][높이 2][너비 2]
      // 너비의 마지막 바이트가 offset+8 이므로 offset+9 바이트까지 있으면 된다.
      if (offset + 9 > view.byteLength) return null;
      const storedHeight = view.getUint16(offset + 5);
      const storedWidth = view.getUint16(offset + 7);
      const swap = orientationSwapsAxes(orientation);
      return {
        width: swap ? storedHeight : storedWidth,
        height: swap ? storedWidth : storedHeight,
        orientation,
      };
    }
    offset += 2 + length;
  }
  return null;
}

export function readImageMetaFromHeader(buffer: ArrayBuffer): ImageMeta | null {
  const view = new DataView(buffer);
  const meta = readPngSize(view) ?? readJpegMeta(view);
  if (!meta || meta.width <= 0 || meta.height <= 0) return null;
  return meta;
}
